import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  Alert,
  IconButton,
  Divider,
  Autocomplete,
  TextField,
  Paper,
  CircularProgress,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  Close,
  CheckCircle,
  Medication,
  Science,
  Description,
  InfoOutlined,
  Delete,
  Assignment,
} from "@mui/icons-material";
import {
  usePrescriptionTemplates,
  PrescriptionTemplate,
} from "@/fluxvet/hooks/usePrescriptionTemplates";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/pt-br";

// Configura dayjs para exibir datas relativas em português
dayjs.extend(relativeTime);
dayjs.locale("pt-br");

// =============================================================================
// TIPOS
// =============================================================================

interface TemplateSelectorProps {
  open: boolean;
  onClose: () => void;
  onApply: (template: PrescriptionTemplate) => void;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Modal para selecionar e aplicar um protocolo de prescrição
 * Usa Autocomplete para seleção inicial, depois exibe preview completo
 * Melhor UX para quando houver muitos protocolos salvos
 */
export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  open,
  onClose,
  onApply,
}) => {
  const { templates, deleteTemplate, getTemplateStats, isLoading } =
    usePrescriptionTemplates();
  const [selectedTemplate, setSelectedTemplate] =
    useState<PrescriptionTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Estados para controlar seleção de itens
  const [selectedTreatmentsIndices, setSelectedTreatmentsIndices] = useState<Set<number>>(
    new Set()
  );
  const [selectedExamsIndices, setSelectedExamsIndices] = useState<Set<number>>(new Set());
  const [selectedReportsIndices, setSelectedReportsIndices] = useState<Set<number>>(new Set());
  const [includeGuidelines, setIncludeGuidelines] = useState<boolean>(false);
  const [selectedQuestionnaireIndices, setSelectedQuestionnaireIndices] =
    useState<Set<number>>(new Set());

  /**
   * Reseta as seleções quando o protocolo muda
   */
  useEffect(() => {
    if (selectedTemplate) {
      // Seleciona todos os itens por padrão quando um novo protocolo é escolhido
      const treatments = selectedTemplate.treatments || [];
      const exams = selectedTemplate.examsOrProcedures || [];
      const reports = selectedTemplate.reports || [];
      const qs = selectedTemplate.questionnaires || [];

      setSelectedTreatmentsIndices(new Set(treatments.map((_, idx) => idx)));
      setSelectedExamsIndices(new Set(exams.map((_, idx) => idx)));
      setSelectedReportsIndices(new Set(reports.map((_, idx) => idx)));
      setIncludeGuidelines(!!selectedTemplate.guidelines);
      setSelectedQuestionnaireIndices(new Set(qs.map((_, idx) => idx)));
    } else {
      // Limpa as seleções se não há protocolo selecionado
      setSelectedTreatmentsIndices(new Set());
      setSelectedExamsIndices(new Set());
      setSelectedReportsIndices(new Set());
      setIncludeGuidelines(false);
      setSelectedQuestionnaireIndices(new Set());
    }
  }, [selectedTemplate]);

  /**
   * Calcula o total de itens selecionados
   */
  const totalSelectedItems = useMemo(() => {
    return (
      selectedTreatmentsIndices.size +
      selectedExamsIndices.size +
      selectedReportsIndices.size +
      (includeGuidelines ? 1 : 0) +
      selectedQuestionnaireIndices.size
    );
  }, [
    selectedTreatmentsIndices,
    selectedExamsIndices,
    selectedReportsIndices,
    includeGuidelines,
    selectedQuestionnaireIndices,
  ]);

  /**
   * Toggle de seleção de tratamento
   */
  const toggleTreatment = (index: number) => {
    setSelectedTreatmentsIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  /**
   * Toggle de seleção de exame
   */
  const toggleExam = (index: number) => {
    setSelectedExamsIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  /**
   * Toggle de seleção de relatório
   */
  const toggleReport = (index: number) => {
    setSelectedReportsIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toggleQuestionnaire = (index: number) => {
    setSelectedQuestionnaireIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) newSet.delete(index);
      else newSet.add(index);
      return newSet;
    });
  };

  /**
   * Aplica o protocolo selecionado com apenas os itens escolhidos
   */
  const handleApply = () => {
    if (!selectedTemplate) return;

    if (totalSelectedItems === 0) {
      return; // Não aplica se nenhum item foi selecionado
    }

    // Cria um template filtrado com apenas os itens selecionados
    const filteredTemplate: PrescriptionTemplate = {
      ...selectedTemplate,
      treatments: selectedTemplate.treatments
        ? selectedTemplate.treatments.filter((_, idx) =>
            selectedTreatmentsIndices.has(idx)
          )
        : undefined,
      examsOrProcedures: selectedTemplate.examsOrProcedures
        ? selectedTemplate.examsOrProcedures.filter((_, idx) =>
            selectedExamsIndices.has(idx)
          )
        : undefined,
      reports: selectedTemplate.reports
        ? selectedTemplate.reports.filter((_, idx) =>
            selectedReportsIndices.has(idx)
          )
        : undefined,
      guidelines: includeGuidelines ? selectedTemplate.guidelines : undefined,
      questionnaires: (() => {
        const list = selectedTemplate.questionnaires?.filter((_, idx) =>
          selectedQuestionnaireIndices.has(idx),
        );
        return list && list.length > 0 ? list : undefined;
      })(),
    };

    onApply(filteredTemplate);
    handleClose();
  };

  /**
   * Abre o dialog de confirmação de exclusão
   */
  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  /**
   * Fecha o dialog de confirmação de exclusão
   */
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  /**
   * Confirma e exclui o protocolo selecionado
   */
  const handleConfirmDelete = async () => {
    if (!selectedTemplate) return;

    try {
      await deleteTemplate(selectedTemplate.id);
      setSelectedTemplate(null);
      handleCloseDeleteDialog();
    } catch (error) {
      console.error("Erro ao excluir protocolo:", error);
      // O erro já é tratado no hook
    }
  };

  /**
   * Fecha o modal e reseta o estado
   */
  const handleClose = () => {
    setSelectedTemplate(null);
    setDeleteDialogOpen(false);
    setSelectedTreatmentsIndices(new Set());
    setSelectedExamsIndices(new Set());
    setSelectedReportsIndices(new Set());
    setIncludeGuidelines(false);
    setSelectedQuestionnaireIndices(new Set());
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircle sx={{ color: "#00A884" }} />
            <Typography variant="h6" fontWeight={600}>
              Aplicar Protocolo de Prescrição
            </Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Informação */}
          <Alert severity="info" icon={<InfoOutlined />}>
            Selecione um protocolo e escolha quais itens deseja aplicar. Os itens
            selecionados serão <strong>adicionados</strong> aos itens já existentes
            no atendimento.
          </Alert>

          {/* Select de protocolos */}
          {isLoading ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fafafa",
                borderRadius: 2,
              }}
            >
              <CircularProgress size={40} sx={{ color: "#00A884" }} />
              <Typography variant="body2" color="text.secondary" mt={2}>
                Carregando protocolos...
              </Typography>
            </Box>
          ) : templates.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                bgcolor: "#fafafa",
                border: "1px dashed #e0e0e0",
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum protocolo salvo
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crie seu primeiro protocolo clicando em "Salvar como Protocolo" na
                tela de prescrições
              </Typography>
            </Box>
          ) : (
            <>
              <Autocomplete
                options={templates}
                value={selectedTemplate}
                onChange={(_, newValue) => {
                  setSelectedTemplate(newValue);
                  setDeleteDialogOpen(false); // Fecha dialog de confirmação ao trocar protocolo
                }}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione um protocolo"
                    placeholder="Digite para buscar..."
                    variant="outlined"
                  />
                )}
                renderOption={(props, option) => {
                  const stats = getTemplateStats(option);
                  return (
                    <Box component="li" {...props}>
                      <Stack spacing={0.5} width="100%">
                        <Typography variant="body1" fontWeight={600}>
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {stats.treatmentsCount > 0 && (
                            <Chip
                              label={`${stats.treatmentsCount} tratamento${
                                stats.treatmentsCount > 1 ? "s" : ""
                              }`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {stats.examsCount > 0 && (
                            <Chip
                              label={`${stats.examsCount} exame${
                                stats.examsCount > 1 ? "s" : ""
                              }`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {stats.reportsCount > 0 && (
                            <Chip
                              label={`${stats.reportsCount} relatório${
                                stats.reportsCount > 1 ? "s" : ""
                              }`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {stats.hasGuidelines && (
                            <Chip
                              label="Orientações"
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                          {stats.questionnairesCount > 0 && (
                            <Chip
                              label={`${stats.questionnairesCount} questionário${
                                stats.questionnairesCount > 1 ? "s" : ""
                              }`}
                              size="small"
                              sx={{ height: 20, fontSize: "0.7rem" }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </Box>
                  );
                }}
                noOptionsText="Nenhum protocolo encontrado"
                PaperComponent={(props) => (
                  <Paper {...props} sx={{ maxHeight: 400, overflow: "auto" }} />
                )}
              />

              {/* Preview do protocolo selecionado */}
              {selectedTemplate && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: "2px solid #00A884",
                    borderRadius: 2,
                    bgcolor: "rgba(0, 168, 132, 0.02)",
                  }}
                >
                  <Stack spacing={2}>
                    {/* Cabeçalho do preview */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight={600}
                          color="#00A884"
                        >
                          {selectedTemplate.name}
                        </Typography>
                        {selectedTemplate.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            mt={0.5}
                          >
                            {selectedTemplate.description}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mt={1}
                        >
                          Criado {dayjs(selectedTemplate.createdAt).fromNow()}
                          {selectedTemplate.updatedAt !==
                            selectedTemplate.createdAt &&
                            ` • Atualizado ${dayjs(
                              selectedTemplate.updatedAt
                            ).fromNow()}`}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={handleOpenDeleteDialog}
                        sx={{
                          textTransform: "none",
                        }}
                      >
                        Excluir
                      </Button>
                    </Stack>

                    <Divider />

                    {/* Instrução de seleção */}
                    <Alert severity="success" icon={<CheckCircle />}>
                      <Typography variant="body2">
                        <strong>{totalSelectedItems}</strong>{" "}
                        {totalSelectedItems === 1 ? "item selecionado" : "itens selecionados"}.
                        Marque ou desmarque os itens que deseja aplicar.
                      </Typography>
                    </Alert>

                    {/* Conteúdo detalhado com checkboxes */}
                    <Stack spacing={2}>
                      {/* Tratamentos */}
                      {selectedTemplate.treatments &&
                        selectedTemplate.treatments.length > 0 && (
                          <Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              mb={1}
                            >
                              <Medication
                                sx={{ color: "#00A884", fontSize: 20 }}
                              />
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                color="#00A884"
                              >
                                Tratamentos ({selectedTreatmentsIndices.size}/
                                {selectedTemplate.treatments.length})
                              </Typography>
                            </Stack>
                            <Stack spacing={0.5} pl={1}>
                              {selectedTemplate.treatments.map(
                                (treatment, idx) => (
                                  <FormControlLabel
                                    key={idx}
                                    control={
                                      <Checkbox
                                        checked={selectedTreatmentsIndices.has(idx)}
                                        onChange={() => toggleTreatment(idx)}
                                        size="small"
                                        sx={{
                                          color: "#00A884",
                                          "&.Mui-checked": {
                                            color: "#00A884",
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                          {treatment.name}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          pl={2}
                                        >
                                          {treatment.dosage}
                                        </Typography>
                                        {treatment.description && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            pl={2}
                                          >
                                            {treatment.description}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                )
                              )}
                            </Stack>
                          </Box>
                        )}

                      {/* Exames/Procedimentos */}
                      {selectedTemplate.examsOrProcedures &&
                        selectedTemplate.examsOrProcedures.length > 0 && (
                          <Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              mb={1}
                            >
                              <Science
                                sx={{ color: "#00A884", fontSize: 20 }}
                              />
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                color="#00A884"
                              >
                                Exames/Procedimentos ({selectedExamsIndices.size}/
                                {selectedTemplate.examsOrProcedures.length})
                              </Typography>
                            </Stack>
                            <Stack spacing={0.5} pl={1}>
                              {selectedTemplate.examsOrProcedures.map(
                                (exam, idx) => (
                                  <FormControlLabel
                                    key={idx}
                                    control={
                                      <Checkbox
                                        checked={selectedExamsIndices.has(idx)}
                                        onChange={() => toggleExam(idx)}
                                        size="small"
                                        sx={{
                                          color: "#00A884",
                                          "&.Mui-checked": {
                                            color: "#00A884",
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                          {exam.description}
                                        </Typography>
                                        {exam.notes && (
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            pl={2}
                                          >
                                            {exam.notes}
                                          </Typography>
                                        )}
                                      </Box>
                                    }
                                  />
                                )
                              )}
                            </Stack>
                          </Box>
                        )}

                      {/* Relatórios */}
                      {selectedTemplate.reports &&
                        selectedTemplate.reports.length > 0 && (
                          <Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              mb={1}
                            >
                              <Description
                                sx={{ color: "#00A884", fontSize: 20 }}
                              />
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                color="#00A884"
                              >
                                Relatórios ({selectedReportsIndices.size}/
                                {selectedTemplate.reports.length})
                              </Typography>
                            </Stack>
                            <Stack spacing={0.5} pl={1}>
                              {selectedTemplate.reports.map((report, idx) => (
                                <FormControlLabel
                                  key={idx}
                                  control={
                                    <Checkbox
                                      checked={selectedReportsIndices.has(idx)}
                                      onChange={() => toggleReport(idx)}
                                      size="small"
                                      sx={{
                                        color: "#00A884",
                                        "&.Mui-checked": {
                                          color: "#00A884",
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {report.content.substring(0, 100)}
                                      {report.content.length > 100 ? "..." : ""}
                                    </Typography>
                                  }
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                      {/* Questionários */}
                      {selectedTemplate.questionnaires &&
                        selectedTemplate.questionnaires.length > 0 && (
                          <Box>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              mb={1}
                            >
                              <Assignment
                                sx={{ color: "#00A884", fontSize: 20 }}
                              />
                              <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                color="#00A884"
                              >
                                Questionários ({selectedQuestionnaireIndices.size}/
                                {selectedTemplate.questionnaires.length})
                              </Typography>
                            </Stack>
                            <Stack spacing={0.5} pl={1}>
                              {selectedTemplate.questionnaires.map((q, idx) => (
                                <FormControlLabel
                                  key={q.questionnaireId}
                                  control={
                                    <Checkbox
                                      checked={selectedQuestionnaireIndices.has(idx)}
                                      onChange={() => toggleQuestionnaire(idx)}
                                      size="small"
                                      sx={{
                                        color: "#00A884",
                                        "&.Mui-checked": {
                                          color: "#00A884",
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" fontWeight={500}>
                                      {q.title || q.name}
                                    </Typography>
                                  }
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}

                      {/* Orientações */}
                      {selectedTemplate.guidelines && (
                        <Box>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={includeGuidelines}
                                onChange={(e) => setIncludeGuidelines(e.target.checked)}
                                size="small"
                                sx={{
                                  color: "#00A884",
                                  "&.Mui-checked": {
                                    color: "#00A884",
                                  },
                                }}
                              />
                            }
                            label={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <InfoOutlined
                                  sx={{ color: "#00A884", fontSize: 20 }}
                                />
                                <Typography
                                  variant="subtitle2"
                                  fontWeight={600}
                                  color="#00A884"
                                >
                                  Incluir Orientações
                                </Typography>
                              </Stack>
                            }
                          />
                          {includeGuidelines && (
                            <Typography
                              variant="body2"
                              pl={5}
                              mt={1}
                              sx={{ whiteSpace: "pre-wrap" }}
                            >
                              {selectedTemplate.guidelines}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Stack>
                </Paper>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />}>
          Cancelar
        </Button>
        <Button
          onClick={handleApply}
          variant="contained"
          startIcon={<CheckCircle />}
          disabled={!selectedTemplate || totalSelectedItems === 0}
          sx={{
            bgcolor: "#00A884",
            "&:hover": { bgcolor: "#008c6e" },
          }}
        >
          Adicionar {totalSelectedItems > 0 ? `(${totalSelectedItems})` : ""} itens
        </Button>
      </DialogActions>

      {/* Dialog de confirmação de exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Delete sx={{ color: "error.main" }} />
            <Typography variant="h6" fontWeight={600}>
              Confirmar Exclusão
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1">
              Tem certeza que deseja excluir o protocolo{" "}
              <strong>"{selectedTemplate?.name}"</strong>?
            </Typography>
            <Alert severity="warning">
              Esta ação não pode ser desfeita. O protocolo será permanentemente
              removido.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} startIcon={<Close />}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<Delete />}
            sx={{
              "&:hover": { bgcolor: "error.dark" },
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default TemplateSelector;
